import type { JobFormData } from '../types';

export type SalaryMode = 'confidential' | 'range';

export type JobFormErrors = { [key: string]: string };

/**
 * Shown on the address search when city or state is missing.
 *
 * The search is the only writer of city / state / country: typing a city and tabbing away fills
 * none of them, because nothing is stored until a suggestion is picked. So "City is required" next
 * to a box that visibly says "Mumbai" reads as a broken form. The message says what to do instead.
 */
export const PICK_CITY_MESSAGE =
    'Pick the city and state from the suggestions. Typed text is not saved until you pick one.';
export const PICK_STATE_MESSAGE =
    'Pick a state. Choose a suggestion that includes the state or region.';

const isPositive = (v: number | null | undefined) =>
    v != null && Number.isFinite(Number(v)) && Number(v) > 0;

/**
 * The create form's required fields. Pure, so it can be tested without rendering 1,300 lines.
 *
 * Mirrors createJob's own check on the backend (JobCreateValidation), which refuses the same job
 * anyway: this exists so the operator finds out on the field rather than from a failed request.
 */
export const validateJobForm = (formData: JobFormData, salaryMode: SalaryMode): JobFormErrors => {
    const errors: JobFormErrors = {};
    if (!formData.category) errors.category = 'Category is required';
    if (!formData.industry) errors.industry = 'Industry is required';
    if (!formData.description) errors.description = 'Description is required';
    if (!formData.city?.trim()) errors.city = PICK_CITY_MESSAGE;
    /*
     * State is required (owner decision 2026-10-20).
     *
     * It was deliberately optional: Phnom Penh is a municipality with no province above it, the
     * place lookup returned an empty state, and requiring it blocked that address. The cost of
     * that leniency turned out to be larger. Google for Jobs reads `addressRegion` from every
     * listing's structured data, and 778 jobs were found missing their address. A city without a
     * region is the commonest way a job lost it. The backend now refuses a new job without a
     * state too, so leaving it optional here would only move the failure to after Create.
     *
     * A place the lookup returns with no region at all is therefore not postable from here. That
     * is the accepted cost, and the message says so on the field rather than blocking silently,
     * which was the half of the Phnom Penh bug that actually hurt.
     */
    if (!formData.state?.trim()) errors.state = PICK_STATE_MESSAGE;
    if (!formData.country) errors.country = 'Country is required';
    if (!formData.title) errors.title = 'Title is required';
    if (formData.isExternalJob ? !formData.companyName : !formData.companyId) {
        errors.companyId = 'Company Name is required';
    }
    if (!formData.jobType) errors.jobType = 'Job type is required';

    /*
     * Salary is a range or an explicit "not disclosed". "Confidential / As per norms" is that
     * explicit choice, and it is the default, so only "Specify Range" can fail: half a range
     * renders on the site as a salary that starts or ends nowhere.
     */
    if (salaryMode === 'range') {
        const minOk = isPositive(formData.salaryMin);
        const maxOk = isPositive(formData.salaryMax);
        if (!minOk) errors.salaryMin = 'Enter a minimum salary above zero';
        if (!maxOk) errors.salaryMax = 'Enter a maximum salary above zero';
        if (minOk && maxOk && Number(formData.salaryMax) < Number(formData.salaryMin)) {
            errors.salaryMax = 'Max salary must be at least the min salary';
        }
    }
    return errors;
};
